import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy } from 'pdfjs-dist';
// Bundled worker served by Vite (also used for the standalone build).
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import type { OcrImage } from './ocr';

let workerConfigured = false;

export function ensurePdfWorkerConfigured(): void {
  if (!workerConfigured) {
    GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
    workerConfigured = true;
  }
}

export interface Renderer {
  pageCount: number;
  renderPage(pageNumber: number): Promise<{ image: OcrImage; width: number; height: number }>;
  destroy(): void;
}

/**
 * Create a pdf.js renderer that renders pages to grayscale canvases.
 * `scale` defaults to 3 which matches the OCR calibration on the sample
 * labels (288 x 432 pt -> 864 x 1296 px).
 */
export async function createPdfRenderer(
  data: ArrayBuffer | Uint8Array,
  scale = 3,
): Promise<Renderer> {
  ensurePdfWorkerConfigured();
  // pdf.js transfers (detaches) the input buffer to its worker, so hand it a
  // private copy and keep the caller's buffer usable for the split pipeline.
  const pdfData = data instanceof Uint8Array ? data.slice() : new Uint8Array(data).slice();
  const doc: PDFDocumentProxy = await getDocument({ data: pdfData }).promise;

  const renderPage = async (pageNumber: number) => {
    const page = await doc.getPage(pageNumber);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('无法创建 Canvas 渲染上下文');
    await page.render({ canvasContext: ctx, viewport }).promise;
    page.cleanup();
    const gray = toGrayscaleCanvas(canvas);
    return { image: gray, width: gray.width, height: gray.height };
  };

  return {
    pageCount: doc.numPages,
    renderPage,
    destroy() {
      void doc.destroy();
    },
  };
}

/** Convert a rendered canvas to a grayscale canvas (with a light contrast stretch). */
export function toGrayscaleCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = source.width;
  out.height = source.height;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) return source;
  ctx.drawImage(source, 0, 0);
  const imageData = ctx.getImageData(0, 0, out.width, out.height);
  const px = imageData.data;
  let min = 255;
  let max = 0;
  for (let i = 0; i < px.length; i += 4) {
    const v = (px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114) | 0;
    px[i] = px[i + 1] = px[i + 2] = v;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const span = Math.max(1, max - min);
  for (let i = 0; i < px.length; i += 4) {
    const v = ((px[i] - min) * 255) / span;
    px[i] = px[i + 1] = px[i + 2] = v;
  }
  ctx.putImageData(imageData, 0, 0);
  return out;
}
